import { api, throwApiError } from "../../../api/api";
import type {
  AdjustmentReason,
  AdjustmentStatus,
  AdjustmentType,
  EffectivePunch,
  SignatureState,
  SignatureStatus,
  TimesheetDay,
  TimesheetTotals,
} from "../../colaborador/api/timekeepingApi";

/**
 * Ponto — telas do DP (feat/ponto). Só ADMIN e DP. Não há rota que crie marcação:
 * a correção é sempre um ajuste.
 */

export type { AdjustmentReason, AdjustmentStatus, AdjustmentType, EffectivePunch, SignatureState, SignatureStatus, TimesheetDay, TimesheetTotals };
export {
  SIGNATURE_LABELS,
  ADJUSTMENT_REASON_LABELS,
  ADJUSTMENT_TYPE_LABELS,
  DAY_FLAG_LABELS,
  DAY_KIND_LABELS,
  duration,
  hhmm,
  timeOf,
} from "../../colaborador/api/timekeepingApi";

export type TimesheetStatus = "ABERTO" | "FECHADO";

export const SIGNATURE_BADGE: Record<SignatureStatus, string> = {
  NAO_FECHADO: "dpBadge--gray",
  PENDENTE: "dpBadge--orange",
  ASSINADO: "dpBadge--green",
  CONTESTADO: "dpBadge--red",
  DESATUALIZADO: "dpBadge--orange",
};

export type SignatureFilter = "PENDENTE" | "ASSINADO" | "CONTESTADO" | "ATRASADO";

/** Motivos que o DP escolhe ao lançar (inclui os que o colaborador não vê). */
export const DP_ADJUSTMENT_REASONS: AdjustmentReason[] = [
  "ESQUECIMENTO",
  "DISPOSITIVO_INDISPONIVEL",
  "SEM_CREDENCIAL",
  "FALHA_CONEXAO",
  "SISTEMA_INDISPONIVEL",
  "MARCACAO_INDEVIDA",
  "MARCACAO_ATRASADA",
  "AUSENCIA_JUSTIFICADA",
  "OUTRO",
];

export const ADJUSTMENT_STATUS_LABELS: Record<AdjustmentStatus, string> = {
  PENDENTE: "Pendente",
  APROVADO: "Aprovado",
  REJEITADO: "Recusado",
};

export const ADJUSTMENT_STATUS_BADGE: Record<AdjustmentStatus, string> = {
  PENDENTE: "dpBadge--orange",
  APROVADO: "dpBadge--green",
  REJEITADO: "dpBadge--gray",
};

/** Bloqueios e avisos do espelho (`código` ou `código:valor`) em texto. */
export const describeIssue = (code: string): string => {
  const [key, value] = code.split(":");
  const date = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.split("-").reverse().join("/") : null;
  switch (key) {
    case "MARCACAO_IMPAR":
      return `Marcação faltando em ${date ?? value}`;
    case "AJUSTE_PENDENTE":
      return `${value} ajuste(s) aguardando decisão`;
    case "SEM_ESCALA_PUBLICADA":
      return `${value} dia(s) sem escala publicada (vale a jornada padrão)`;
    case "FORA_DA_LOJA":
      return `${value} dia(s) com marcação longe da loja`;
    case "ABANDONO": {
      // `ABANDONO:n:AAAA-MM-DD`: a sequência pode ter começado no mês anterior.
      const since = code.split(":")[2];
      const sinceText = since && /^\d{4}-\d{2}-\d{2}$/.test(since) ? ` (desde ${since.slice(8, 10)}/${since.slice(5, 7)})` : "";
      return `${value} faltas seguidas${sinceText} — possível abandono de emprego`;
    }
    case "HE_ACIMA_DO_LIMITE":
      return `${value} dia(s) com mais de 2h extras`;
    case "INTERJORNADA_VIOLADA":
      return `${value} dia(s) com menos de 11h de descanso entre jornadas`;
    case "INTERVALO_INSUFICIENTE":
      return `${value} dia(s) com intervalo menor que 1h`;
    case "MARCACAO_EM_AUSENCIA":
      return `${value} dia(s) com marcação em férias ou afastamento`;
    case "SEM_LOCALIZACAO":
      return `${value} dia(s) com marcação sem localização`;
    default:
      return code;
  }
};

export type DpTimesheetRow = {
  id: string;
  contractId: string;
  year: number;
  month: number;
  status: TimesheetStatus;
  computedAt: string | null;
  closedAt: string | null;
  expectedMinutes: number;
  workedMinutes: number;
  overtimeWorkdayMinutes: number;
  overtimeRestDayMinutes: number;
  nightComputedMinutes: number;
  lateMinutes: number;
  earlyLeaveMinutes: number;
  absenceDays: number;
  justifiedAbsenceDays: number;
  dsrLostCount: number;
  workedDaysCount: number;
  originalPunchCount: number;
  adjustedPunchCount: number;
  daysWithoutRoster: number;
  daysOutsideStore: number;
  maxConsecutiveAbsences: number;
  pendingAdjustments: number;
  blockers: string[];
  warnings: string[];
  employee: { id: string; fullName: string; socialName: string | null; cpf: string | null };
  contract: { registrationNumber: string | null };
  store: { id: string; nomeFantasia: string } | null;
  signature: SignatureState | null;
};

export type DpTimesheetList = {
  page: number;
  limit: number;
  total: number;
  items: DpTimesheetRow[];
  totals: {
    employees: number;
    open: number;
    closed: number;
    blocked: number;
    expectedMinutes: number;
    workedMinutes: number;
    overtimeWorkdayMinutes: number;
    overtimeRestDayMinutes: number;
    nightComputedMinutes: number;
    lateMinutes: number;
    earlyLeaveMinutes: number;
    absenceDays: number;
    dsrLostCount: number;
    originalPunchCount: number;
    adjustedPunchCount: number;
    daysWithoutRoster: number;
    daysOutsideStore: number;
    pendingAdjustments: number;
    adjustedRatio: number;
    signed: number;
    contested: number;
    signaturePending: number;
    signatureOverdue: number;
  };
};

export type DpPunch = {
  id: string;
  nsr: number;
  punchedAt: string;
  localDay: string;
  source: "WEB" | "MOBILE" | "KIOSK";
  ipAddress: string | null;
  userAgent: string | null;
  skewSeconds: number | null;
  latitude: number | null;
  longitude: number | null;
  accuracyMeters: number | null;
  distanceToStoreMeters: number | null;
  storeRadiusMeters: number | null;
  outsideStore: boolean | null;
  /** Selfie tirada na marcação; abre por `fetchPunchPhotoApi`. */
  hasPhoto: boolean;
  hash: string;
};

export type DpAdjustment = {
  id: string;
  employeeId: string;
  contractId: string;
  day: string;
  type: AdjustmentType;
  reasonCode: AdjustmentReason;
  reason: string | null;
  targetPunchId: string | null;
  targetAdjustmentId: string | null;
  proposedAt: string | null;
  minutes: number | null;
  attachmentAssetId: string | null;
  attachmentFileName: string | null;
  batchId: string | null;
  requestedByEmployee: boolean;
  status: AdjustmentStatus;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  requestedByUser: { id: string; nomeCompleto: string } | null;
  decidedBy: { id: string; nomeCompleto: string } | null;
  annulledBy: { id: string; createdAt?: string; reason?: string | null } | null;
  targetPunch?: { id: string; nsr: number; punchedAt: string } | null;
};

export type DpTimesheetDetail = DpTimesheetRow & {
  paidMinutes: number;
  nightClockMinutes: number;
  businessDaysCount: number;
  restDaysCount: number;
  reopenedAt: string | null;
  reopenReason: string | null;
  snapshotHash: string | null;
  closedBy: { id: string; nomeCompleto: string } | null;
  reopenedBy: { id: string; nomeCompleto: string } | null;
  days: TimesheetDay[];
  contract: { id: string; registrationNumber: string | null; admissionDate: string };
  payrollClosed: boolean;
  provisionedAt: string | null;
  monthEnded: boolean;
  punches: DpPunch[];
  adjustments: DpAdjustment[];
  signature: SignatureState;
  signatureHistory: Array<{ kind: "ACEITE" | "CONTESTACAO"; hashShort: string; text: string | null; ipAddress: string | null; createdAt: string }>;
};

export type PresenceState = "PREVISTO" | "PRESENTE" | "EM_INTERVALO" | "SAIU" | "FALTOU" | "FOLGA" | "FERIAS" | "AFASTADO" | "SEM_ESCALA";

export const PRESENCE_LABELS: Record<PresenceState, string> = {
  PREVISTO: "Previsto",
  PRESENTE: "Presente",
  EM_INTERVALO: "Em intervalo",
  SAIU: "Saiu",
  FALTOU: "Faltou",
  FOLGA: "Folga",
  FERIAS: "Férias",
  AFASTADO: "Afastado",
  SEM_ESCALA: "Sem escala",
};

export const PRESENCE_BADGE: Record<PresenceState, string> = {
  PREVISTO: "dpBadge--gray",
  PRESENTE: "dpBadge--green",
  EM_INTERVALO: "dpBadge--blue",
  SAIU: "dpBadge--gray",
  FALTOU: "dpBadge--red",
  FOLGA: "dpBadge--gray",
  FERIAS: "dpBadge--gray",
  AFASTADO: "dpBadge--gray",
  SEM_ESCALA: "dpBadge--orange",
};

export type PresenceRow = {
  employee: { id: string; fullName: string; socialName: string | null };
  contractId: string;
  expected: { shift: "MANHA" | "NOITE" | null; startMinute: number | null; endMinute: number | null; source: string } | null;
  expectedKind: string;
  punches: Array<{ id: string; at: string; minuteOfDay: number; outsideStore: boolean | null; hasPhoto: boolean }>;
  state: PresenceState;
  lateMinutes: number;
  outsideStore: boolean;
};

export type Presence = {
  date: string;
  when: "PASSADO" | "HOJE" | "FUTURO";
  enabled: boolean;
  store: { id: string; nomeFantasia: string };
  rows: PresenceRow[];
  totals: Record<PresenceState, number> & { employees: number; expected: number; outsideStore: number; late: number };
};

export type EmployeeTimekeepingSummary = {
  enabled: boolean;
  startDate: string | null;
  admissionDate: string | null;
  provisionedAt: string | null;
  lateAccessDays: number;
  months: Array<{ year: number; month: number; contractId: string | null; timesheet: (DpTimesheetRow & { signature: SignatureState }) | null }>;
  vacationPeriods: Array<{ id: string; acquisitionStart: string; acquisitionEnd: string; unjustifiedAbsences: number; absencesFromPonto: number }>;
};

export type DpQueueItem = DpAdjustment & {
  employee: { id: string; fullName: string; socialName: string | null };
  contract: { registrationNumber: string | null };
  store: { id: string; nomeFantasia: string } | null;
  dayContext: {
    day: string;
    kind: string;
    shift: string | null;
    startMinute: number | null;
    endMinute: number | null;
    expectedMinutes: number;
    workedMinutes: number;
    flags: string[];
    effectivePunches: EffectivePunch[];
    timesheetStatus: TimesheetStatus;
  } | null;
};

export type DpQueue = {
  page: number;
  limit: number;
  total: number;
  byReason: Array<{ reasonCode: AdjustmentReason; count: number }>;
  items: DpQueueItem[];
};

export type DpAdjustmentPayload = {
  day: string;
  type: Exclude<AdjustmentType, "ANULACAO">;
  reasonCode: AdjustmentReason;
  reason?: string;
  minuteOfDay?: number;
  minutes?: number;
  targetPunchId?: string;
  attachmentAssetId?: string;
  attachmentFileName?: string;
};

export type BulkPayload = {
  reasonCode: AdjustmentReason;
  reason?: string;
  from?: string;
  to?: string;
  days?: Array<{ day: string; marks: number[] }>;
  dryRun?: boolean;
  attachmentAssetId?: string;
  attachmentFileName?: string;
};

export type BulkPlan = { dryRun: true; days: Array<{ day: string; marks: number[]; skipped: string | null }> };

// ── Chamadas ────────────────────────────────────────────────────────────────

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

async function call<T>(fallback: string, run: () => Promise<{ data: T }>): Promise<T> {
  try {
    const { data } = await run();
    return data;
  } catch (error) {
    throwApiError(error, fallback);
    throw error;
  }
}

/** Erro 409 do fechamento traz a lista de bloqueios; a mensagem sozinha não basta. */
export class TimesheetBlockedError extends Error {
  constructor(message: string, public blockers: string[]) {
    super(message);
  }
}

export type TimesheetListParams = {
  year: number;
  month: number;
  storeId?: string;
  withoutStore?: boolean;
  /** Centro de custo da versão de contrato vigente hoje. */
  costCenterId?: string;
  status?: string;
  signature?: SignatureFilter;
  q?: string;
  page?: number;
  limit?: number;
};

/** CSV do relatório como blob (um link direto não mandaria o token). */
export const exportTimesheetsApi = (token: string, params: TimesheetListParams) =>
  call<Blob>("Erro ao exportar o CSV.", () =>
    api.get("/dp/timesheets/export", {
      ...auth(token),
      params: { ...params, page: undefined, limit: undefined, withoutStore: params.withoutStore ? "true" : undefined },
      responseType: "blob",
    }),
  );

/** PDF do espelho; o erro também chega como blob e é lido aqui. */
export const fetchTimesheetPdfApi = async (token: string, contractId: string, year: number, month: number): Promise<Blob> => {
  try {
    const { data } = await api.get(`/dp/contracts/${contractId}/timesheets/${year}/${month}/pdf`, { ...auth(token), responseType: "blob" });
    return data;
  } catch (error: any) {
    const body = error?.response?.data;
    if (body instanceof Blob) {
      let message = "Erro ao gerar o PDF do espelho.";
      try {
        const parsed = JSON.parse(await body.text());
        if (typeof parsed?.message === "string") message = parsed.message;
      } catch {
        // corpo não é JSON: fica a mensagem padrão
      }
      throw new Error(message);
    }
    throwApiError(error, "Erro ao gerar o PDF do espelho.");
    throw error;
  }
};

export const getPresenceApi = (token: string, storeId: string, date?: string) =>
  call<Presence>("Erro ao carregar a presença do dia.", () => api.get("/dp/timesheets/presence", { ...auth(token), params: { storeId, date } }));

export const getEmployeeTimekeepingSummaryApi = (token: string, employeeId: string) =>
  call<EmployeeTimekeepingSummary>("Erro ao carregar o resumo do ponto.", () => api.get(`/dp/employees/${employeeId}/timekeeping-summary`, auth(token)));

export const listTimesheetsApi = (
  token: string,
  params: TimesheetListParams,
) =>
  call<DpTimesheetList>("Erro ao carregar o ponto do mês.", () =>
    api.get("/dp/timesheets", { ...auth(token), params: { ...params, withoutStore: params.withoutStore ? "true" : undefined } }),
  );

export const getTimesheetsSummaryApi = (token: string) =>
  call<{
    enabled: boolean;
    startDate: string | null;
    pendingAdjustments: number;
    openPrevMonth: number;
    prevMonth: { year: number; month: number };
    contested: number;
    unsignedOverdue: number;
    signatureDeadlineDays: number;
  }>(
    "Erro ao carregar o resumo do ponto.",
    () => api.get("/dp/timesheets/summary", auth(token)),
  );

export const recomputeMonthApi = (token: string, year: number, month: number, storeId?: string) =>
  call<{ created: number; recomputed: number; failed: number }>("Erro ao recalcular o mês.", () =>
    api.post(`/dp/timesheets/${year}/${month}/recompute`, {}, { ...auth(token), params: storeId ? { storeId } : {} }),
  );

export const closeManyTimesheetsApi = (token: string, payload: { year: number; month: number; storeId?: string; contractIds?: string[] }) =>
  call<{ closed: number; blocked: Array<{ contractId: string; employeeName: string; message: string; blockers: string[] }> }>(
    "Erro ao fechar os espelhos.",
    () => api.post("/dp/timesheets/close-many", payload, auth(token)),
  );

export const getTimesheetApi = (token: string, contractId: string, year: number, month: number) =>
  call<DpTimesheetDetail>("Erro ao carregar o espelho.", () => api.get(`/dp/contracts/${contractId}/timesheets/${year}/${month}`, auth(token)));

export const recomputeTimesheetApi = (token: string, contractId: string, year: number, month: number) =>
  call<DpTimesheetDetail>("Erro ao recalcular o espelho.", () => api.post(`/dp/contracts/${contractId}/timesheets/${year}/${month}/recompute`, {}, auth(token)));

export const closeTimesheetApi = async (token: string, contractId: string, year: number, month: number) => {
  try {
    const { data } = await api.post(`/dp/contracts/${contractId}/timesheets/${year}/${month}/close`, {}, auth(token));
    return data;
  } catch (error: any) {
    const body = error?.response?.data;
    if (body?.blockers?.length) throw new TimesheetBlockedError(body.message ?? "O espelho tem pendências.", body.blockers);
    throwApiError(error, "Erro ao fechar o espelho.");
    throw error;
  }
};

export const reopenTimesheetApi = (token: string, contractId: string, year: number, month: number, reason: string) =>
  call("Erro ao reabrir o espelho.", () => api.post(`/dp/contracts/${contractId}/timesheets/${year}/${month}/reopen`, { reason }, auth(token)));

export const listAdjustmentsApi = (
  token: string,
  params: { status?: AdjustmentStatus; storeId?: string; contractId?: string; reasonCode?: AdjustmentReason; from?: string; to?: string; q?: string; page?: number; limit?: number },
) => call<DpQueue>("Erro ao carregar os ajustes.", () => api.get("/dp/time-adjustments", { ...auth(token), params }));

export const createAdjustmentApi = (token: string, contractId: string, payload: DpAdjustmentPayload) =>
  call<DpAdjustment>("Erro ao lançar o ajuste.", () => api.post(`/dp/contracts/${contractId}/time-adjustments`, payload, auth(token)));

export const createBulkAdjustmentApi = <T = { batchId: string; created: number; days: number; skipped: Array<{ day: string; reason: string }> }>(
  token: string,
  contractId: string,
  payload: BulkPayload,
) => call<T>("Erro ao lançar o lote.", () => api.post(`/dp/contracts/${contractId}/time-adjustments/bulk`, payload, auth(token)));

export const approveAdjustmentApi = (token: string, id: string, note?: string) =>
  call("Erro ao aprovar o pedido.", () => api.post(`/dp/time-adjustments/${id}/approve`, { note }, auth(token)));

export const rejectAdjustmentApi = (token: string, id: string, note: string) =>
  call("Erro ao recusar o pedido.", () => api.post(`/dp/time-adjustments/${id}/reject`, { note }, auth(token)));

export const approveManyAdjustmentsApi = (token: string, ids: string[]) =>
  call<{ approved: number; failed: Array<{ id: string; message: string }> }>("Erro ao aprovar os pedidos.", () =>
    api.post("/dp/time-adjustments/approve-many", { ids }, auth(token)),
  );

export const annulAdjustmentApi = (token: string, id: string, reason: string) =>
  call("Erro ao anular o ajuste.", () => api.post(`/dp/time-adjustments/${id}/annul`, { reason }, auth(token)));

export const annulBatchApi = (token: string, batchId: string, reason: string) =>
  call<{ annulled: number }>("Erro ao anular o lote.", () => api.post(`/dp/time-adjustments/batch/${batchId}/annul`, { reason }, auth(token)));

export const uploadAdjustmentFileApi = (token: string, file: File) => {
  const form = new FormData();
  form.append("file", file);
  return call<{ assetId: string; fileName: string | null }>("Erro ao enviar o arquivo.", () =>
    api.post("/dp/time-adjustments/files", form, { headers: { ...auth(token).headers, "Content-Type": "multipart/form-data" } }),
  );
};

/** Selfie da marcação (só ADMIN e DP), pelo id da marcação. */
export const fetchPunchPhotoApi = async (token: string, punchId: string): Promise<Blob> => {
  try {
    const { data } = await api.get(`/dp/time-punches/${punchId}/photo`, { ...auth(token), responseType: "blob" });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao abrir a foto da marcação.");
    throw error;
  }
};

export const fetchAdjustmentFileApi = async (token: string, id: string): Promise<Blob> => {
  try {
    const { data } = await api.get(`/dp/time-adjustments/${id}/file`, { ...auth(token), responseType: "blob" });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao abrir o anexo.");
    throw error;
  }
};

// ── Portaria 671 (ADR 0042): registro de ponto, arquivos oficiais, comprovante ──

export type RepEmployerStatus = {
  employerId: string;
  legalName: string;
  cnpj: string;
  started: boolean;
  startedAt: string | null;
  lastNsr: number;
};

export type RepStatus = { employers: RepEmployerStatus[]; warnings: string[] };

export type AejPendency = { code: string; contractId?: string; employeeId?: string; name?: string; day?: string; message: string };

export type ChainReport = {
  checkedAt: string;
  official: Array<{ employerId: string; legalName: string; records: number; punches: number; brokenNsr: number | null; missingNsr: number | null }>;
  internal: { checkedEmployees: number; checkedPunches: number; broken: Array<{ employeeId: string; name: string; nsr: number }> };
  ok: boolean;
};

/** Erro de arquivo vem como blob: lê a mensagem e, no AEJ, a lista de pendências (422). */
export class OfficialFileError extends Error {
  constructor(message: string, public readonly pendencies: AejPendency[] = []) {
    super(message);
  }
}

async function blobError(error: any, fallback: string): Promise<never> {
  const body = error?.response?.data;
  if (body instanceof Blob) {
    let message = fallback;
    let pendencies: AejPendency[] = [];
    try {
      const parsed = JSON.parse(await body.text());
      if (typeof parsed?.message === "string") message = parsed.message;
      if (Array.isArray(parsed?.pendencies)) pendencies = parsed.pendencies;
    } catch {
      // corpo não é JSON: fica a mensagem padrão
    }
    throw new OfficialFileError(message, pendencies);
  }
  throwApiError(error, fallback);
  throw error;
}

const fileNameOf = (disposition: string | undefined, fallback: string) => /filename="([^"]+)"/.exec(disposition ?? "")?.[1] ?? fallback;

export const getRepStatusApi = (token: string) => call<RepStatus>("Erro ao carregar o registro de ponto.", () => api.get("/dp/rep/status", auth(token)));

export const startRepApi = (token: string, employerId: string) =>
  call<{ employerId: string; createdEmployer: boolean; included: number }>("Erro ao iniciar o registro de ponto.", () =>
    api.post(`/dp/rep/${employerId}/start`, {}, auth(token)),
  );

export const verifyChainsApi = (token: string) => call<ChainReport>("Erro ao conferir as marcações.", () => api.get("/dp/rep/verify", auth(token)));

/** AFD ou AEJ do período, como blob com o nome oficial do arquivo. */
export const fetchOfficialFileApi = async (
  token: string,
  kind: "afd" | "aej",
  params: { employerId: string; from: string; to: string },
): Promise<{ blob: Blob; fileName: string }> => {
  try {
    const response = await api.get(`/dp/rep/${kind}`, { ...auth(token), params, responseType: "blob" });
    return { blob: response.data, fileName: fileNameOf(response.headers?.["content-disposition"], `${kind.toUpperCase()}.txt`) };
  } catch (error) {
    return blobError(error, kind === "afd" ? "Erro ao gerar o AFD." : "Erro ao gerar o AEJ.");
  }
};

/** Comprovante de Registro de Ponto do Trabalhador (PDF) de uma marcação. */
export const fetchPunchReceiptApi = async (token: string, punchId: string): Promise<Blob> => {
  try {
    const { data } = await api.get(`/dp/time-punches/${punchId}/receipt`, { ...auth(token), responseType: "blob" });
    return data;
  } catch (error) {
    return blobError(error, "Erro ao gerar o comprovante.");
  }
};
